import * as Sentry from "@sentry/react";
import * as React from "react";

import { InvalidLayoutDefinitionError } from "@/shared/domain/layouts/LayoutDefinition";
import { UnsupportedLayoutSchemaVersion } from "@/shared/domain/layouts/schemas";
import { LoggerService } from "@/shared/services";
import { actions, useVizDispatch } from "@/shared/state/visualization";
import { cyclingColorScale } from "@/shared/state/visualization/CyclingColorScale";

import { layoutToVizConfig } from "../../layouts/LayoutToVizConfig";
import { useWorkspaceLoaderData } from "../../loader";
import { EphemeralWorkspaceState } from "../../WorkspaceCtx/EphemeralWorkspaceState";
import { selectAllWorkspaceTopicTrees } from "../../WorkspaceCtx/hooks";
import { HydrationError } from "../HydrationError";

/**
 * Loads the active layout into the visualizer when it changes.
 */
export function useLayoutHydration(workspaceContext: EphemeralWorkspaceState) {
  const {
    activeLayout,
    isDataLoading,
    setDataMatchingErrors,
    setWorkspaceSnapshot,
    setIsHydrating,
    skipLayoutRehydration,
  } = workspaceContext;

  const dispatchVizAction = useVizDispatch();
  const workspaceResult = useWorkspaceLoaderData();
  const topicTrees = selectAllWorkspaceTopicTrees(workspaceContext);
  const [error, setError] = React.useState<HydrationError | null>(null);

  // Track if this is the first layout load
  const isInitialLoad = React.useRef(true);

  const loadActiveLayout = React.useCallback(
    function loadLayout(): void {
      LoggerService.log("Loading active layout", activeLayout);

      // Reset the color scale so that panels get new colors when the layout changes
      cyclingColorScale.reset();

      const { panels, layout, errors } = layoutToVizConfig(activeLayout, {
        topicTrees,
      });

      dispatchVizAction(actions.replacePanelsAndLayout(panels, layout));
      setDataMatchingErrors(errors);
      setWorkspaceSnapshot({ panels, layout });
      setIsHydrating(false);
    },
    [
      activeLayout,
      dispatchVizAction,
      setDataMatchingErrors,
      setIsHydrating,
      setWorkspaceSnapshot,
      topicTrees,
    ],
  );

  const handleLayoutError = React.useCallback((e: unknown) => {
    if (e instanceof InvalidLayoutDefinitionError) {
      setError(
        new HydrationError(
          "invalid-layout-definition",
          "Failed to load workspace from layout because it is malformed. Try a different layout.",
          { cause: e },
        ),
      );
    } else if (e instanceof UnsupportedLayoutSchemaVersion) {
      setError(
        new HydrationError(
          "unsupported-layout-schema-version",
          "Failed to load workspace from layout because the layout schema version is unsupported. Try a different layout.",
          { cause: e },
        ),
      );
    } else {
      setError(
        new HydrationError(
          "change-layout-error",
          "Failed to load workspace from layout. Try again.",
          { cause: e },
        ),
      );
    }
  }, []);

  React.useEffect(() => {
    if (isDataLoading) {
      return;
    }

    // Handle initial load
    if (isInitialLoad.current) {
      isInitialLoad.current = false;

      // If no layout to resolve, just finish hydration
      if (workspaceResult?.type !== "layout") {
        setIsHydrating(false);
        return;
      }
    }

    // Skip reload if flag is set
    if (skipLayoutRehydration) {
      return;
    }

    try {
      Sentry.startSpan(
        {
          name: "loadActiveLayout",
          op: "ui.task",
          attributes: {
            active_layout_id: activeLayout.layout_id,
            active_layout_name: activeLayout.name,
          },
        },
        loadActiveLayout,
      );
    } catch (e) {
      handleLayoutError(e);
    }
  }, [
    activeLayout,
    isDataLoading,
    loadActiveLayout,
    setIsHydrating,
    skipLayoutRehydration,
    workspaceResult?.type,
    handleLayoutError,
  ]);

  return { error };
}
